import { useCallback, useRef, useState } from 'react'
import { VIDEO_EXTENSIONS, VIDEO_MAX_BYTES, isVideoFile } from '../../lib/videoTypes'
import { useT, type TVars } from '../../i18n/useT'
import { LoadingSpinner } from '../ui/LoadingSpinner'

const ALLOWED_EXTENSIONS = ['.mp3', '.m4a', '.wav', '.ogg', '.wma']
const MAX_SIZE = 75 * 1024 * 1024

export interface UploadProcessing {
  label: string
  /** null → indeterminate spinner; 0..1 → progress bar */
  ratio: number | null
}

interface FileUploadProps {
  file: File | null
  /** What the person picked, when that is not what gets uploaded. For a
   * video the upload is the extracted track (`clip.m4a`, 40 MB) and the pick
   * was `clip.avi`, 1.5 GB — showing the former makes people wonder whether
   * they chose the wrong thing, right before they spend a transcription.
   * Name and size travel together: one of each would describe no real file. */
  picked?: { name: string; size: number }
  onFileChange: (file: File | null) => void
  credits: number
  /** A registered free account: its balance is a monthly allowance, not a
   * dwindling one-off — the wording has to say so */
  monthlyCredits?: boolean
  error?: string | null
  /** Set while a picked video is having its audio track extracted */
  processing?: UploadProcessing | null
  /** A transcription is running: the file may not be swapped out from under
   * it, and clearing the field would not stop it anyway */
  busy?: boolean
}

// Returned as a catalog key + vars so the message follows the UI language
function validateFile(file: File): { key: string; vars?: TVars } | null {
  // Before anything else, and before the video branch returns early: a
  // zero-byte file used to sail through to the gateway, which refused it
  // several seconds later. Nothing here needs the network to know better.
  if (file.size === 0) {
    return { key: 'err.fileEmpty' }
  }
  if (isVideoFile(file.name)) {
    if (file.size > VIDEO_MAX_BYTES) {
      return { key: 'err.videoTooLarge' }
    }
    return null
  }
  const ext = '.' + file.name.split('.').pop()?.toLowerCase()
  if (!ALLOWED_EXTENSIONS.includes(ext)) {
    return {
      key: 'err.unsupportedFormat',
      vars: { audio: ALLOWED_EXTENSIONS.join(', '), video: VIDEO_EXTENSIONS.join(', ') },
    }
  }
  if (file.size > MAX_SIZE) {
    return { key: 'err.fileTooLarge' }
  }
  return null
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
}

export function FileUpload({ file, picked, onFileChange, credits, monthlyCredits, error, processing, busy }: FileUploadProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const [dragActive, setDragActive] = useState(false)
  const [validationError, setValidationError] = useState<{ key: string; vars?: TVars } | null>(null)

  const handleFile = useCallback(
    (f: File) => {
      const err = validateFile(f)
      if (err) {
        setValidationError(err)
        onFileChange(null)
      } else {
        setValidationError(null)
        onFileChange(f)
      }
    },
    [onFileChange],
  )

  /** Locked while a transcription runs, along with the click and the ✕: the
   * drop zone is a third way into the same field, and swapping the file under
   * a run in flight changes what is on screen without changing what is being
   * uploaded. */
  const locked = !!processing || !!busy

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragActive(false)
    if (locked) return
    const f = e.dataTransfer.files[0]
    if (f) handleFile(f)
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (locked) return
    const f = e.target.files?.[0]
    if (f) handleFile(f)
  }

  const displayError = validationError ? t(validationError.key, validationError.vars) : error

  return (
    <div>
      <label className="block text-sm font-medium text-text-secondary mb-1.5">{t('form.fileLabel')}</label>
      <div
        onDragOver={(e) => { e.preventDefault(); if (!locked) setDragActive(true) }}
        onDragLeave={() => setDragActive(false)}
        onDrop={handleDrop}
        onClick={() => { if (!locked) inputRef.current?.click() }}
        className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all duration-200 ${
          dragActive
            ? 'border-primary bg-primary/5'
            : displayError
            ? 'border-error/50 bg-error/5'
            : file
            ? 'border-success/50 bg-success/5'
            : 'border-border hover:border-text-muted hover:bg-surface-hover'
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={[...ALLOWED_EXTENSIONS, ...VIDEO_EXTENSIONS].join(',')}
          onChange={handleChange}
          className="hidden"
        />

        {processing ? (
          <div className="py-2">
            {processing.ratio !== null ? (
              <div className="w-full max-w-xs mx-auto bg-surface-hover rounded-full h-2 overflow-hidden mb-3">
                <div
                  className="bg-primary h-full transition-all duration-300"
                  style={{ width: `${Math.round(processing.ratio * 100)}%` }}
                />
              </div>
            ) : (
              <div className="mb-3 flex justify-center"><LoadingSpinner size="sm" /></div>
            )}
            <p className="text-sm text-text-secondary">{processing.label}</p>
            <p className="text-xs text-text-muted mt-1">{t('form.videoStaysLocal')}</p>
          </div>
        ) : file ? (
          <div className="flex items-center justify-center gap-3">
            <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center shrink-0">
              <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
              </svg>
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-medium text-text truncate">{picked?.name ?? file.name}</p>
              <p className="text-xs text-text-muted">{formatFileSize(picked?.size ?? file.size)}</p>
            </div>
            {/* Locked while a transcription is running: it only ever cleared
                the form, so the parts went on being uploaded behind an empty
                field. Stopping is what the Cancel beside the bar is for. */}
            <button
              type="button"
              disabled={locked}
              onClick={(e) => {
                e.stopPropagation()
                onFileChange(null)
                setValidationError(null)
                if (inputRef.current) inputRef.current.value = ''
              }}
              className="p-1 rounded hover:bg-surface-hover text-text-muted hover:text-text transition-colors disabled:opacity-40 disabled:pointer-events-none"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : (
          <>
            <div className="w-12 h-12 bg-surface-hover rounded-xl flex items-center justify-center mx-auto mb-3">
              <svg className="w-6 h-6 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
              </svg>
            </div>
            <p className="text-sm text-text-muted mb-2">
              {monthlyCredits
                ? credits === 1
                  ? t('credits.remainingMonthlyOne')
                  : t('credits.remainingMonthly', { count: credits })
                : credits === 1
                  ? t('credits.remainingOne')
                  : t('credits.remaining', { count: credits })}
            </p>
            <p className="text-sm text-text-secondary">
              <span className="font-medium text-primary">{t('form.clickToUpload')}</span> {t('form.orDrag')}
            </p>
            <p className="text-xs text-text-muted mt-1">{t('form.fileHint')}</p>
          </>
        )}
      </div>

      {displayError && (
        <p className="mt-1.5 text-xs text-error">{displayError}</p>
      )}
    </div>
  )
}
